import { Outlet, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  LogOut,
  Menu,
  X,
  ArrowLeft,
  LayoutDashboard,
  LayoutGrid,
  Palette,
  BarChart3,
  MessageSquare,
  ClipboardList,
  Image,
  Settings,
  Send,
  Users,
  ChevronRight,
  QrCode,
  CheckCircle,
  Tv2,
  Wallet,
  Lock,
} from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/store/auth.store';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { MaoIntroModal } from '@/components/mao';
import { EventFeatures } from '@/hooks/use-event-limits';
import { FeatureLockedDialog } from '@/components/feature-gate/FeatureLockedDialog';

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  exact?: boolean;
  featureKey?: keyof EventFeatures;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const getEventNavGroups = (eventId: string): NavGroup[] => [
  {
    title: 'General',
    items: [
      { href: `/app/events/${eventId}`, label: 'Eveniment', icon: Calendar, color: 'lavender', exact: true },
      { href: `/app/events/${eventId}/budget`, label: 'Buget', icon: Wallet, color: 'terracotta' },
      { href: `/app/events/${eventId}/settings`, label: 'Setări', icon: Settings, color: 'sage' },
    ],
  },
  {
    title: 'Invitați & Participanți',
    items: [
      { href: `/app/events/${eventId}/guests`, label: 'Oaspeți', icon: Users, color: 'sage' },
      { href: `/app/events/${eventId}/invitations`, label: 'Invitații', icon: Send, color: 'terracotta' },
      { href: `/app/events/${eventId}/seating`, label: 'Aranjare mese', icon: LayoutGrid, color: 'dusty-blue' },
      { href: `/app/events/${eventId}/responses`, label: 'Răspunsuri', icon: ClipboardList, color: 'sage' },
    ],
  },
  {
    title: 'Design & Conținut',
    items: [
      { href: `/app/events/${eventId}/templates`, label: 'Modele', icon: Palette, color: 'dusty-blue' },
      { href: `/app/events/${eventId}/design`, label: 'Personalizare', icon: LayoutDashboard, color: 'primary' },
      { href: `/app/events/${eventId}/questions`, label: 'Întrebări', icon: MessageSquare, color: 'lavender' },
    ],
  },
  {
    title: 'Media',
    items: [
      { href: `/app/events/${eventId}/albums`, label: 'Album', icon: Image, color: 'terracotta', featureKey: 'mediaAlbum' },
      { href: `/app/events/${eventId}/upload-portal`, label: 'Portal QR', icon: QrCode, color: 'dusty-blue', featureKey: 'uploadPortal' },
      { href: `/app/events/${eventId}/moderation`, label: 'Moderare', icon: CheckCircle, color: 'sage', featureKey: 'moderationQueue' },
      { href: `/app/events/${eventId}/wall`, label: 'Live Wall', icon: Tv2, color: 'terracotta', featureKey: 'liveWall' },
    ],
  },
  {
    title: 'Analiză',
    items: [
      { href: `/app/events/${eventId}/statistics`, label: 'Statistici', icon: BarChart3, color: 'primary', featureKey: 'analytics' },
    ],
  },
];

export function EventLayout() {
  const { user, logout } = useAuth();
  const { accessToken } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: eventId } = useParams<{ id: string }>();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lockedDialogFeature, setLockedDialogFeature] = useState<keyof EventFeatures | null>(null);

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<any>(`/events/${eventId}`, { token: accessToken! }),
    enabled: !!accessToken && !!eventId,
  });

  // Fetch package/plan information (includes features for lock icons)
  const { data: planLimits } = useQuery({
    queryKey: ['event-limits', eventId],
    queryFn: () => api.get<{ packageKey: string; planName: string; features: Record<string, boolean> }>(`/payments/event/${eventId}/limits`, { token: accessToken! }),
    enabled: !!accessToken && !!eventId,
    staleTime: 5 * 60 * 1000,
  });

  const navGroups = eventId ? getEventNavGroups(eventId) : [];
  const allNavItems = navGroups.flatMap(group => group.items);
  
  const currentNavItem = allNavItems.find((item) => 
    item.exact 
      ? location.pathname === item.href 
      : location.pathname.startsWith(item.href)
  );


  return (
    <div className="min-h-screen flex bg-secondary/20">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-premium transform transition-transform duration-500 ease-in-out lg:translate-x-0 lg:static border-r border-border/40',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="h-full flex flex-col">
          {/* Back to Dashboard */}
          <div className="h-20 flex items-center justify-between px-6 border-b border-border/40">
            <button
              onClick={() => navigate('/app/events')}
              className="flex items-center gap-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
            >
              <div className="w-8 h-8 rounded-xl bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </div>
              <span className="tracking-tight">Toate Evenimentele</span>
            </button>
            <button 
              className="lg:hidden p-2 rounded-xl hover:bg-secondary transition-colors" 
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Event Info Card */}
          <div className="px-5 py-6 border-b border-border/40">
            {isLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-secondary rounded w-3/4"></div>
                <div className="h-3 bg-secondary rounded w-1/2"></div>
              </div>
            ) : event ? (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif font-bold text-lg tracking-tight leading-tight line-clamp-2">
                    {event.name}
                  </h3>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{new Date(event.eventDate).toLocaleDateString('ro-RO', { 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric'
                    })}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Eveniment negăsit</p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-5 overflow-y-auto scrollbar-thin">
            {navGroups.map((group) => (
              <div key={group.title}>
                <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 mb-2">
                  {group.title}
                </p>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.exact
                      ? location.pathname === item.href
                      : location.pathname.startsWith(item.href);
                    const isLocked = item.featureKey && planLimits?.features
                      ? !planLimits.features[item.featureKey]
                      : false;
                    if (isLocked) {
                      return (
                        <button
                          key={item.href}
                          type="button"
                          className={cn(
                            'flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 group relative overflow-hidden w-full text-left',
                            'hover:bg-secondary/80 text-muted-foreground hover:text-foreground',
                          )}
                          onClick={() => {
                            setSidebarOpen(false);
                            setLockedDialogFeature(item.featureKey!);
                          }}
                        >
                          <Icon className="h-4 w-4 transition-colors duration-300 group-hover:text-foreground" />
                          <span className="font-medium text-sm tracking-tight flex-1">{item.label}</span>
                          <Lock className="h-3 w-3 flex-shrink-0 text-amber-500" />
                        </button>
                      );
                    }
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-300 group relative overflow-hidden',
                          isActive
                            ? 'bg-foreground text-background shadow-premium'
                            : 'hover:bg-secondary/80 text-muted-foreground hover:text-foreground',
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className={cn('h-4 w-4 transition-colors duration-300',
                          isActive ? 'text-background' : 'group-hover:text-foreground'
                        )} />
                        <span className="font-medium text-sm tracking-tight flex-1">{item.label}</span>
                        {isActive && (
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-primary rounded-l-full" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Quick access to Dashboard */}
          <div className="p-5 border-t border-border/40">
            <Link 
              to="/app/dashboard"
              className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-secondary/50 hover:bg-secondary transition-colors group"
            >
              <LayoutDashboard className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors tracking-tight">
                Dashboard Principal
              </span>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground/50" />
            </Link>
          </div>

          {/* User section */}
          <div className="p-5 border-t border-border/40">
            <div className="flex items-center gap-3 px-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                <span className="text-xs font-bold text-primary tracking-tighter">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate tracking-tight">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              className="w-full justify-start gap-3 h-10 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors" 
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
              <span className="text-sm tracking-tight">Deconectare</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-white/70 backdrop-blur-xl border-b border-border/40 flex items-center justify-between px-6 lg:px-12 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2.5 -ml-2 rounded-2xl hover:bg-secondary transition-colors" 
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-3">
              {/* Breadcrumb */}
              <Link 
                to="/app/events"
                className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 hover:text-primary transition-colors hidden sm:block"
              >
                Evenimente
              </Link>
              <ChevronRight className="w-3 h-3 text-muted-foreground/40 hidden sm:block" />
              <div className="flex flex-col">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 leading-none mb-1 sm:hidden">
                  {event?.name || 'Eveniment'}
                </span>
                <h2 className="text-lg font-serif font-bold tracking-tight text-foreground leading-none">
                  {currentNavItem?.label || 'Eveniment'}
                </h2>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {event && (
              <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-2xl bg-secondary/50 border border-border/40">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground truncate max-w-[150px]">
                  {event.name}
                </span>
              </div>
            )}
            <NotificationsBell />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto scrollbar-hide">
          <Outlet />
        </main>
      </div>

      {/* Feature locked dialog */}
      <FeatureLockedDialog
        feature={lockedDialogFeature}
        eventId={eventId!}
        open={!!lockedDialogFeature}
        onOpenChange={(open) => { if (!open) setLockedDialogFeature(null); }}
      />

      {/* Mao AI Assistant Introduction Modal */}
      <MaoIntroModal />
    </div>
  );
}
